import React, { useMemo, useState, useEffect } from "react";
import { FileText, MessageSquare, Target, TrendingUp, ShieldCheck, Star, AlertTriangle, Send, Trophy, TrendingDown, CheckCircle2, Pencil, Calendar, Sparkles, Plane } from "lucide-react";
import RevenueChart from "@/components/RevenueChart";
import MessageThread from "@/components/MessageThread";
import { SectionHead, Delta } from "@/ui";
import {
  eur, pct, score, rank, deltaPct,
  STRATEGY_LABEL, STATUS_LABEL, STARTING_PARAMS, URGENCY, MONTHS_IT,
} from "@/format";

const BR = "#E05A2C", INK = "#FAF7F2", INK2 = "#C4BBB0", MUT = "#857C72";
const CARD = "#16110D", BORDER = "#2A241F";

function Card({ children, className = "", ...rest }) {
  return (
    <div className={`rounded-xl border p-6 ${className}`} style={{ background: CARD, borderColor: BORDER }} {...rest}>
      {children}
    </div>
  );
}

function Kpi({ label, value, delta, sub }) {
  return (
    <div className="rounded-xl border p-5" style={{ background: CARD, borderColor: BORDER }}>
      <div className="eyebrow" style={{ color: MUT }}>{label}</div>
      <div className="mt-2 font-display text-2xl" style={{ color: INK }}>{value}</div>
      {(delta || sub) ? (
        <div className="mt-1">
          {delta ? <div><Delta delta={delta} /> <span className="font-mono text-[10px]" style={{ color: MUT }}>vs anno prec.</span></div> : null}
          {sub ? <div className="font-mono text-[10px] mt-0.5" style={{ color: MUT }}>{sub}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

export default function ClientDashboardView({
  overview,
  monthly = [],
  yearly = [],
  messages = [],
  pricing = [],
  opportunities = [],
  onSendMessage = null,
  onReplyMessage = null,
  onDownloadPdf,
  onDownloadAnnualPdf = null,
  onDownloadPricingPdf = null,
  onDownloadOpportunitiesPdf = null,
  onPricingFeedback = null,
  onRefreshAir = null,
}) {
  const structure = overview?.structure || {};
  const startingData = overview?.starting_data || null;
  const [tab, setTab] = useState("monthly");

  // Year scoping: the client picks a year; both Monthly and Yearly views show only that year.
  const yearsAvail = useMemo(() => {
    const s = new Set();
    monthly.forEach((p) => s.add(p.year));
    yearly.forEach((p) => s.add(p.year));
    return [...s].sort((a, b) => b - a);
  }, [monthly, yearly]);
  const [year, setYear] = useState(null);
  useEffect(() => {
    if (yearsAvail.length && (year == null || !yearsAvail.includes(year))) setYear(yearsAvail[0]);
  }, [yearsAvail, year]);

  const monthlyForYear = useMemo(() => monthly.filter((p) => p.year === year), [monthly, year]);
  const yearSel = useMemo(() => yearly.find((p) => p.year === year) || null, [yearly, year]);

  const list = useMemo(() => (
    tab === "monthly" ? monthlyForYear : tab === "yearly" ? (yearSel ? [yearSel] : []) : []
  ), [tab, monthlyForYear, yearSel]);
  const [selId, setSelId] = useState(null);

  const selected = useMemo(() => {
    if (!list.length) return null;
    return list.find((p) => p.id === selId) || list[0];
  }, [list, selId]);

  // Chart: monthly shows the 12 months of the selected year (missing ones greyed); yearly shows the 12 months.
  const chart = useMemo(() => {
    if (tab === "monthly") {
      const byMonth = {};
      monthlyForYear.forEach((p) => { byMonth[p.month] = p; });
      return {
        data: Array.from({ length: 12 }, (_, i) => {
          const mo = i + 1;
          const p = byMonth[mo];
          return {
            key: p ? p.id : `empty-${mo}`,
            label: MONTHS_IT[i].slice(0, 3),
            value: p ? p.revenue : null,
            pending: !p,
          };
        }),
        activeKey: selected?.id || null,
      };
    }
    if (!selected) return { data: [], activeKey: null };
    const months = selected.months || [];
    return {
      data: months.map((m) => ({
        key: `m-${m.month}`,
        label: m.label,
        value: m.revenue,
        goalMet: m.revenue_target != null && m.revenue != null && m.revenue >= m.revenue_target,
        goalPct: (m.revenue_target != null && m.revenue_target > 0 && m.revenue != null)
          ? Math.round((m.revenue / m.revenue_target) * 100)
          : null,
      })),
      activeKey: null,
    };
  }, [selected, tab, monthlyForYear]);

  // Best / worst month of the selected year (by revenue), for the yearly view.
  const bestWorst = useMemo(() => {
    if (tab !== "yearly" || !selected) return null;
    const withData = (selected.months || []).filter((m) => m.revenue != null && m.revenue > 0);
    if (withData.length < 2) return null;
    const sorted = [...withData].sort((a, b) => b.revenue - a.revenue);
    const mi = (lbl) => MONTHS_IT[sorted[lbl].month - 1];
    return { best: { label: mi(0), revenue: sorted[0].revenue }, worst: { label: mi(sorted.length - 1), revenue: sorted[sorted.length - 1].revenue } };
  }, [tab, selected]);

  // YTD revenue for the glance card.
  const ytd = useMemo(() => {
    if (!selected) return null;
    if (tab === "yearly") {
      return { value: selected.revenue, ly: selected.comparison?.yoy?.revenue ?? null };
    }
    const y = selected.year, m = selected.month;
    const sameYear = monthly.filter((p) => p.year === y && p.month <= m);
    const lyYear = monthly.filter((p) => p.year === y - 1 && p.month <= m);
    return {
      value: sameYear.reduce((s, p) => s + (p.revenue || 0), 0),
      ly: lyYear.length ? lyYear.reduce((s, p) => s + (p.revenue || 0), 0) : null,
    };
  }, [selected, tab, monthly]);

  const yoy = selected?.comparison?.yoy || null;
  const prev = selected?.comparison?.prev || null;

  // Yearly goal summary: how many months hit their target + overall % of annual goal.
  const annualGoal = useMemo(() => {
    if (tab !== "yearly" || !selected) return null;
    const months = selected.months || [];
    const withTarget = months.filter((m) => m.revenue_target != null && m.revenue_target > 0);
    if (!withTarget.length) return null;
    const reached = withTarget.filter((m) => m.revenue != null && m.revenue >= m.revenue_target);
    const pct = selected.revenue_target ? Math.round((selected.revenue / selected.revenue_target) * 100) : null;
    return { total: withTarget.length, reached: reached.length, pct };
  }, [tab, selected]);

  const bookingAlert = useMemo(() => {
    if (tab !== "monthly" || !selected) return null;
    const cur = selected.booking_position, base = prev?.booking_position;
    if (cur == null || base == null) return null;
    const d = base - cur;
    if (Math.abs(d) <= 3) return null;
    return d > 0
      ? `In risalita: guadagnate ${Math.abs(d)} posizioni su Booking rispetto al mese scorso.`
      : `Attenzione: perse ${Math.abs(d)} posizioni su Booking rispetto al mese scorso.`;
  }, [tab, selected, prev]);

  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);
  const submitMsg = async (e) => {
    e.preventDefault();
    if (!onSendMessage || !msgText.trim() || sending) return;
    setSending(true);
    try {
      await onSendMessage(msgText.trim());
      setMsgText("");
    } finally {
      setSending(false);
    }
  };

  const focus = structure.current_focus;
  const note = structure.specialist_note;
  const clientAnalysis = structure.client_analysis;

  const Tab = ({ id, children }) => (
    <button
      onClick={() => { setTab(id); setSelId(null); }}
      className="rounded-full px-5 py-2 text-sm font-medium transition-colors"
      style={{
        background: tab === id ? BR : "transparent",
        color: tab === id ? "#fff" : INK2,
        border: `1px solid ${tab === id ? BR : BORDER}`,
      }}
    >
      {children}
    </button>
  );

  return (
    <div className="theme-brand" style={{ background: "#12100E", minHeight: "100%" }}>
      <div className="max-w-5xl mx-auto px-5 md:px-6 py-8">
        {/* Structure header */}
        <div className="eyebrow" style={{ color: BR }}>La tua struttura</div>
        <h1 className="mt-2 font-display text-3xl md:text-4xl uppercase" style={{ color: INK }}>{structure.name}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm" style={{ color: MUT }}>
          {structure.location ? <span>{structure.location}</span> : null}
          <span className="rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest"
            style={{ background: "#1C1815", color: structure.status === "OPERATIVA" ? "#4F9D69" : "#D4A359", border: `1px solid ${BORDER}` }}>
            {STATUS_LABEL[structure.status] || structure.status}
          </span>
        </div>

        {/* Pricing strategy banner */}
        <div className="mt-5 rounded-xl px-5 py-4 flex items-center gap-3"
          style={{ background: "#2B1B15", border: `1px solid #4D2C20` }}>
          <TrendingUp className="h-5 w-5" style={{ color: BR }} />
          <span className="font-medium" style={{ color: INK }}>{STRATEGY_LABEL[structure.pricing_strategy]}</span>
        </div>

        {/* L'analisi del tuo specialista (pubblicata dall'admin) */}
        {clientAnalysis?.text ? (
          <div className="mt-5 rounded-xl border p-6" style={{ background: CARD, borderColor: BORDER }} data-testid="client-analysis">
            <div className="flex items-center gap-2 eyebrow" style={{ color: BR }}>
              <Sparkles className="h-4 w-4" /> L'analisi del tuo specialista
            </div>
            <p className="mt-3 whitespace-pre-line" style={{ color: INK2 }}>{clientAnalysis.text}</p>
          </div>
        ) : null}

        {/* Tabs */}
        <div className="mt-7 flex flex-wrap gap-2">
          <Tab id="monthly">Mensile</Tab>
          <Tab id="yearly">Annuale</Tab>
          {pricing.length ? <Tab id="strategia">Strategia Prezzi</Tab> : null}
          {opportunities.length ? <Tab id="opportunita">Opportunità</Tab> : null}
          <Tab id="baseline">Situazione iniziale</Tab>
        </div>

        {tab === "baseline" ? (
          <BaselineView startingData={startingData} latest={monthly[0]} />
        ) : tab === "strategia" ? (
          <PricingStrategyView pricing={pricing} onDownloadPricingPdf={onDownloadPricingPdf} onPricingFeedback={onPricingFeedback} />
        ) : tab === "opportunita" ? (
          <OpportunitiesView opportunities={opportunities} pricing={pricing} onDownloadOpportunitiesPdf={onDownloadOpportunitiesPdf} onRefreshAir={onRefreshAir} />
        ) : (
          <>
            {/* Year selector — scopes both Monthly and Yearly to the chosen year */}
            {yearsAvail.length ? (
              <div className="mt-6 flex items-center gap-2 flex-wrap" data-testid="year-selector">
                <span className="text-xs font-mono uppercase tracking-widest" style={{ color: MUT }}>Anno</span>
                {yearsAvail.map((y) => (
                  <button key={y} data-testid={`year-${y}`} onClick={() => { setYear(y); setSelId(null); }}
                    className="rounded-full px-4 py-1.5 text-sm font-medium transition-colors"
                    style={{
                      background: y === year ? BR : "transparent",
                      color: y === year ? "#fff" : INK2,
                      border: `1px solid ${y === year ? BR : BORDER}`,
                    }}>
                    {y}
                  </button>
                ))}
              </div>
            ) : null}

            {!selected ? (
              <div className="mt-8 text-center py-16 rounded-xl border" style={{ borderColor: BORDER, color: MUT }}>
                {yearsAvail.length
                  ? (tab === "monthly" ? "Nessun dato mensile per quest'anno." : "Nessun dato disponibile per quest'anno.")
                  : "Nessun periodo disponibile al momento. Il tuo specialista pubblicherà presto i primi dati."}
              </div>
            ) : (
              <>
                {/* Month chips (monthly only) */}
                {tab === "monthly" ? (
                  <div className="mt-5 flex gap-2 overflow-x-auto pb-2">
                    {list.map((p) => (
                      <button key={p.id} onClick={() => setSelId(p.id)}
                        className="whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors"
                        style={{
                          background: p.id === selected.id ? "#2B1B15" : "transparent",
                          color: p.id === selected.id ? BR : MUT,
                          border: `1px solid ${p.id === selected.id ? "#4D2C20" : BORDER}`,
                        }}>
                        {p.period_label}
                      </button>
                    ))}
                  </div>
                ) : null}

            {/* 01 Colpo d'occhio */}
            <div className="mt-8">
              <SectionHead n="01" title="Colpo d'occhio" hint={`Periodo: ${selected.period_label}`} />
              {tab === "yearly" ? (() => {
                const annRev = selected.annual_revenue != null ? selected.annual_revenue : selected.revenue;
                const annLy = selected.annual_ly_revenue != null ? selected.annual_ly_revenue : null;
                const diff = annLy != null ? annRev - annLy : null;
                const diffDir = diff == null ? "none" : diff > 0 ? "up" : diff < 0 ? "down" : "flat";
                const diffColor = diffDir === "up" ? "#4F9D69" : diffDir === "down" ? "#D4604A" : MUT;
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="annual-glance">
                    <Kpi label="Fatturato annuo" value={eur(annRev)} />
                    <Kpi label="Fatturato anno scorso" value={annLy != null ? eur(annLy) : "—"} />
                    <div className="rounded-xl border p-5" style={{ background: CARD, borderColor: BORDER }} data-testid="annual-diff">
                      <div className="eyebrow" style={{ color: MUT }}>Differenza</div>
                      {diff != null ? (
                        <>
                          <div className="mt-2 font-display text-2xl" style={{ color: diffColor }}>
                            {diff > 0 ? "+" : diff < 0 ? "−" : ""}{eur(Math.abs(diff))}
                          </div>
                          <div className="mt-1"><Delta delta={deltaPct(annRev, annLy)} /> <span className="font-mono text-[10px]" style={{ color: MUT }}>vs anno scorso</span></div>
                        </>
                      ) : (
                        <div className="mt-2 text-sm" style={{ color: MUT }}>Nessun dato dell'anno scorso</div>
                      )}
                    </div>
                  </div>
                );
              })() : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Kpi label="Fatturato mese" value={eur(selected.revenue)}
                    delta={yoy ? deltaPct(selected.revenue, yoy.revenue) : null}
                    sub={yoy && yoy.revenue != null ? `anno prec. ${eur(yoy.revenue)}` : null} />
                  <Kpi label="Fatturato anno (YTD)" value={eur(ytd?.value || 0)}
                    delta={ytd?.ly != null ? deltaPct(ytd.value, ytd.ly) : null}
                    sub={ytd?.ly != null ? `anno prec. ${eur(ytd.ly)}` : null} />
                  <Kpi label="Occupazione" value={pct(selected.occupancy)}
                    delta={yoy ? deltaPct(selected.occupancy, yoy.occupancy) : null} />
                </div>
              )}
              {bestWorst ? (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="best-worst">
                  <div className="rounded-xl border p-4 flex items-center gap-3" style={{ background: CARD, borderColor: BORDER }}>
                    <Trophy className="h-5 w-5 shrink-0" style={{ color: "#4F9D69" }} />
                    <div>
                      <div className="eyebrow" style={{ color: MUT }}>Mese top</div>
                      <div className="mt-0.5" style={{ color: INK }}>{bestWorst.best.label} · <span style={{ color: "#4F9D69" }}>{eur(bestWorst.best.revenue)}</span></div>
                    </div>
                  </div>
                  <div className="rounded-xl border p-4 flex items-center gap-3" style={{ background: CARD, borderColor: BORDER }}>
                    <TrendingDown className="h-5 w-5 shrink-0" style={{ color: "#D4604A" }} />
                    <div>
                      <div className="eyebrow" style={{ color: MUT }}>Mese più basso</div>
                      <div className="mt-0.5" style={{ color: INK }}>{bestWorst.worst.label} · <span style={{ color: "#D4604A" }}>{eur(bestWorst.worst.revenue)}</span></div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* 02 Andamento fatturato */}
            <div className="mt-10">
              <SectionHead n="02" title="Andamento fatturato" hint={tab === "yearly" ? "I 12 mesi dell'anno" : "Tocca un mese per vedere il dettaglio"} />
              <Card>
                <RevenueChart
                  data={chart.data}
                  activeKey={chart.activeKey}
                  onSelect={tab === "monthly" ? (k) => { if (!String(k).startsWith("empty-")) setSelId(k); } : undefined}
                  formatValue={(v) => eur(v)}
                />
                {tab === "yearly" && chart.data.some((d) => d.goalMet) ? (
                  <p className="mt-4 text-xs flex items-center gap-2" style={{ color: MUT }} data-testid="goal-legend">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#3FB863" }} />
                    Mese con obiettivo di fatturato raggiunto
                  </p>
                ) : null}
                {tab === "monthly" && chart.data.some((d) => d.pending) ? (
                  <p className="mt-4 text-xs flex items-center gap-2" style={{ color: MUT }} data-testid="pending-legend">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#3A322B" }} />
                    I mesi in grigio sono in attesa di pubblicazione
                  </p>
                ) : null}
              </Card>
            </div>

            {/* 03 Focus */}
            {focus?.text ? (
              <div className="mt-10">
                <SectionHead n="03" title="Il nostro focus adesso" />
                <Card>
                  <p className="font-display text-lg uppercase" style={{ color: BR }}>{focus.text}</p>
                  {focus.reason ? <p className="mt-3" style={{ color: INK2 }}>{focus.reason}</p> : null}
                </Card>
              </div>
            ) : null}

            {/* 04 Cosa sta succedendo ora */}
            <div className="mt-10">
              <SectionHead n="04" title="Cosa sta succedendo ora" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <div className="flex items-center gap-2 eyebrow" style={{ color: MUT }}>
                    <Target className="h-4 w-4" style={{ color: BR }} /> Obiettivo del periodo
                  </div>
                  {selected.revenue_target ? (() => {
                    const p = Math.min(100, Math.round((selected.revenue / selected.revenue_target) * 100));
                    return (
                      <div className="mt-4">
                        <div className="flex justify-between text-sm" style={{ color: INK }}>
                          <span>{eur(selected.revenue)}</span>
                          <span style={{ color: MUT }}>obiettivo {eur(selected.revenue_target)}</span>
                        </div>
                        <div className="mt-2 h-3 rounded-full overflow-hidden" style={{ background: "#2A241F" }}>
                          <div className="h-full rounded-full" style={{ width: `${p}%`, background: `linear-gradient(90deg, ${BR}, #F26B3A)` }} />
                        </div>
                        <div className="mt-2 font-mono text-xs" style={{ color: BR }}>{p}% raggiunto</div>
                        {annualGoal ? (
                          <div className="mt-3 pt-3 flex items-center gap-2 text-sm" style={{ borderTop: `1px solid ${BORDER}`, color: INK2 }} data-testid="annual-goal-summary">
                            <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#3FB863" }} />
                            Obiettivo raggiunto in <span className="font-display" style={{ color: INK }}>{annualGoal.reached}</span> {annualGoal.reached === 1 ? "mese" : "mesi"} su {annualGoal.total}
                          </div>
                        ) : null}
                      </div>
                    );
                  })() : <p className="mt-4 text-sm" style={{ color: MUT }}>Nessun obiettivo impostato per questo periodo.</p>}
                </Card>
                <Card>
                  <div className="eyebrow" style={{ color: MUT }}>{tab === "yearly" ? "La nota dell'anno" : "La nota dello specialista"}</div>
                  {tab === "yearly" ? (
                    selected.annual_note ? <p className="mt-3" style={{ color: INK2 }}>{selected.annual_note}</p>
                      : <p className="mt-3 text-sm" style={{ color: MUT }}>Nessuna nota per quest'anno.</p>
                  ) : (
                    note?.text ? <p className="mt-3" style={{ color: INK2 }}>{note.text}</p>
                      : <p className="mt-3 text-sm" style={{ color: MUT }}>Nessuna nota al momento.</p>
                  )}
                </Card>
              </div>
            </div>

            {/* 05 Performance */}
            <div className="mt-10">
              <SectionHead n="05" title="Performance e soddisfazione ospiti" />
              {tab !== "yearly" && bookingAlert ? (
                <div className="mb-4 rounded-xl px-4 py-3 flex items-center gap-3 text-sm"
                  style={{ background: "#2B1B15", border: `1px solid #4D2C20`, color: INK }}>
                  <AlertTriangle className="h-4 w-4" style={{ color: BR }} /> {bookingAlert}
                </div>
              ) : null}
              {tab === "yearly" ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="annual-performance">
                  <Kpi label="Occupazione" value={pct(selected.occupancy)} />
                  <Kpi label="ADR" value={eur(selected.adr)} />
                  <Kpi label="RevPAR" value={eur(selected.revpar)} />
                  <Kpi label="Qualità OTA" value={selected.ota_quality != null ? score(selected.ota_quality) : "—"} />
                  <Kpi label="Recensioni" value={selected.review_score != null ? score(selected.review_score) : "—"} />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Kpi label="ADR" value={eur(selected.adr)} delta={yoy ? deltaPct(selected.adr, yoy.adr) : null} />
                  <Kpi label="RevPAR" value={eur(selected.revpar)} delta={yoy ? deltaPct(selected.revpar, yoy.revpar) : null} />
                  <Kpi label="Qualità OTA" value={selected.ota_quality != null ? score(selected.ota_quality) : "—"} />
                  <Kpi label="Posizione Booking" value={selected.booking_position != null ? rank(selected.booking_position) : "—"} />
                  <Kpi label="Recensioni" value={selected.review_score != null ? score(selected.review_score) : "—"} />
                </div>
              )}
            </div>

            {/* Competitor sul mercato (visibile al cliente) */}
            {(structure.competitors && structure.competitors.length > 0) ? (
              <div className="mt-10">
                <SectionHead n="✦" title="Competitor sul mercato" hint="I competitor selezionati dal team e il loro ADR" />
                <Card>
                  <div className="space-y-2">
                    {structure.competitors.map((c, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 rounded-lg border px-4 py-3" style={{ borderColor: BORDER }}>
                        <div>
                          <div className="font-medium" style={{ color: INK }}>{c.name}</div>
                          {c.note ? <div className="text-xs mt-0.5" style={{ color: MUT }}>{c.note}</div> : null}
                        </div>
                        <div className="font-mono text-lg" style={{ color: c.adr != null ? BR : MUT }}>
                          {c.adr != null ? eur(c.adr) : "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                  {(() => {
                    const vals = structure.competitors.map((c) => c.adr).filter((v) => v != null);
                    const avg = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
                    if (avg == null) return null;
                    return (
                      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-1 border-t pt-4 text-sm" style={{ borderColor: BORDER }}>
                        <span style={{ color: MUT }}>ADR medio competitor: <span style={{ color: INK }}>{eur(avg)}</span></span>
                        {selected.adr ? <span style={{ color: MUT }}>Il tuo ADR: <span style={{ color: BR }}>{eur(selected.adr)}</span></span> : null}
                      </div>
                    );
                  })()}
                </Card>
              </div>
            ) : null}

            {/* 06 La nota del mese + Report */}
            <div className="mt-10">
              <SectionHead n="06" title={tab === "yearly" ? "Le note dei mesi" : "La nota del mese"} />
              <Card>
                {tab === "yearly" ? (
                  (selected.monthly_notes && selected.monthly_notes.length) ? (
                    <div className="space-y-4">
                      {selected.monthly_notes.map((mn, i) => (
                        <div key={i}>
                          <div className="eyebrow" style={{ color: BR }}>{mn.label}</div>
                          <p className="mt-1" style={{ color: INK2 }}>{mn.notes}</p>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm" style={{ color: MUT }}>Nessuna nota per quest'anno.</p>
                ) : (
                  selected.notes ? <p style={{ color: INK2 }}>{selected.notes}</p>
                    : <p className="text-sm" style={{ color: MUT }}>Nessuna nota per questo mese.</p>
                )}
                <div className="mt-6">
                  <button onClick={() => {
                      if (tab === "yearly") { (onDownloadAnnualPdf || onDownloadPdf) && (onDownloadAnnualPdf ? onDownloadAnnualPdf(selected) : onDownloadPdf(selected)); }
                      else { onDownloadPdf && onDownloadPdf(selected); }
                    }}
                    data-testid="download-pdf-btn"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
                    style={{ border: `1px solid ${BORDER}`, color: INK }}>
                    <FileText className="h-4 w-4" style={{ color: BR }} /> {tab === "yearly" ? "Scarica il report annuale PDF" : "Scarica il report PDF"}
                  </button>
                </div>
              </Card>
            </div>

            {/* 07 Segnalazioni */}
            <div className="mt-10 mb-16">
              <SectionHead n="07" title="Segnalazioni" hint="Scrivi al tuo specialista: ti risponderà al più presto." />
              <Card>
                {onSendMessage ? (
                  <form onSubmit={submitMsg}>
                    <textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} rows={3}
                      placeholder="Scrivi qui la tua segnalazione o domanda…"
                      className="w-full rounded-xl bg-[#1C1815] border px-4 py-3 outline-none focus:border-[#E05A2C] transition-colors"
                      style={{ borderColor: BORDER, color: INK }} />
                    <div className="mt-3 flex justify-end">
                      <button type="submit" disabled={sending || !msgText.trim()}
                        className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-white disabled:opacity-50"
                        style={{ background: BR }}>
                        <Send className="h-4 w-4" /> {sending ? "Invio…" : "Invia segnalazione"}
                      </button>
                    </div>
                  </form>
                ) : (
                  <p className="text-sm" style={{ color: MUT }}>Anteprima in sola lettura: l'invio di segnalazioni è disponibile solo per il cliente.</p>
                )}

                <div className="mt-6 space-y-3">
                  {messages.length === 0 ? (
                    <p className="text-sm" style={{ color: MUT }}>Nessuna segnalazione inviata.</p>
                  ) : messages.map((m) => (
                    <MessageThread
                      key={m.id}
                      message={m}
                      mineSender="client"
                      theme="dark"
                      onReply={onReplyMessage ? (text) => onReplyMessage(m.id, text) : null}
                    />
                  ))}
                </div>
              </Card>
            </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    unread: { label: "Ricevuta", color: "#D4A359" },
    read: { label: "Letta", color: "#C4BBB0" },
    replied: { label: "Risposta", color: "#4F9D69" },
  };
  const s = map[status] || map.unread;
  return <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: s.color }}>{s.label}</span>;
}

function BaselineView({ startingData, latest }) {
  if (!startingData || !startingData.params) {
    return (
      <div className="mt-10 text-center py-16 rounded-xl border" style={{ borderColor: BORDER, color: MUT }}>
        La situazione iniziale non è ancora stata condivisa.
      </div>
    );
  }
  const todayFor = (key) => {
    if (!latest) return null;
    const map = { adr: latest.adr, occupancy_pct: latest.occupancy, conversion_rate: latest.conversion, monthly_revenue: latest.revenue };
    return key in map ? map[key] : null;
  };
  return (
    <div className="mt-8">
      <SectionHead n="✦" title="Situazione iniziale" hint={startingData.recorded_date ? `Rilevata il ${new Date(startingData.recorded_date).toLocaleDateString("it-IT")}` : null} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {STARTING_PARAMS.map((p) => {
          const item = startingData.params[p.key];
          if (!item || item.value == null) return null;
          const u = URGENCY[item.urgency] || URGENCY.low;
          const today = todayFor(p.key);
          return (
            <div key={p.key} className="rounded-xl border p-5" style={{ background: CARD, borderColor: BORDER }}>
              <div className="flex items-center justify-between">
                <span className="eyebrow" style={{ color: MUT }}>{p.label}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{ color: u.color, border: `1px solid ${u.color}55` }}>{u.label}</span>
              </div>
              <div className="mt-3 font-display text-2xl" style={{ color: INK }}>
                {item.value.toLocaleString("it-IT")}<span className="text-base" style={{ color: MUT }}>{p.unit}</span>
              </div>
              {today != null ? (
                <div className="mt-2 text-sm" style={{ color: MUT }}>
                  Oggi: <span style={{ color: INK }}>{today.toLocaleString("it-IT")}{p.unit}</span>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {startingData.notes ? (
        <div className="mt-6 rounded-xl border p-6" style={{ background: CARD, borderColor: BORDER }}>
          <div className="eyebrow" style={{ color: BR }}>La lettura del team</div>
          <p className="mt-3" style={{ color: INK2 }}>{startingData.notes}</p>
        </div>
      ) : null}
    </div>
  );
}


function PricingStrategyView({ pricing = [], onDownloadPricingPdf, onPricingFeedback }) {
  const years = useMemo(() => pricing.map((p) => p.year), [pricing]);
  const [year, setYear] = useState(null);
  useEffect(() => {
    if (years.length && (year == null || !years.includes(year))) setYear(years[0]);
  }, [years, year]);
  const strat = useMemo(() => pricing.find((p) => p.year === year) || null, [pricing, year]);

  const [mode, setMode] = useState(null); // null | "confirm" | "changes"
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { setMode(null); setMsg(""); }, [year]);

  if (!strat) {
    return (
      <div className="mt-10 text-center py-16 rounded-xl border" style={{ borderColor: BORDER, color: MUT }}>
        Nessuna strategia prezzi pubblicata al momento.
      </div>
    );
  }

  const byMonth = {};
  (strat.rows || []).forEach((r) => { (byMonth[r.month] = byMonth[r.month] || []).push(r); });
  const fb = strat.client_feedback;

  const submit = async (state) => {
    if (!onPricingFeedback || sending) return;
    setSending(true);
    try {
      await onPricingFeedback(year, state, state === "changes_requested" ? msg.trim() : "");
      setMode(null); setMsg("");
    } finally { setSending(false); }
  };

  return (
    <div className="mt-8" data-testid="client-pricing">
      {/* Year selector */}
      {years.length > 1 ? (
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="pricing-year-selector">
          <span className="text-xs font-mono uppercase tracking-widest" style={{ color: MUT }}>Anno</span>
          {years.map((y) => (
            <button key={y} data-testid={`pricing-year-${y}`} onClick={() => setYear(y)}
              className="rounded-full px-4 py-1.5 text-sm font-medium transition-colors"
              style={{ background: y === year ? BR : "transparent", color: y === year ? "#fff" : INK2, border: `1px solid ${y === year ? BR : BORDER}` }}>
              {y}
            </button>
          ))}
        </div>
      ) : null}

      <SectionHead n="✦" title={`Strategia prezzi ${strat.year}`} hint="Tariffe consigliate dal tuo specialista, mese per mese" />

      {/* Specialist note + general comment */}
      {strat.client_note ? (
        <div className="rounded-xl px-5 py-4 mb-4" style={{ background: "#2B1B15", border: `1px solid #4D2C20` }} data-testid="pricing-client-note">
          <div className="eyebrow" style={{ color: BR }}>Nota del tuo specialista</div>
          <p className="mt-2" style={{ color: INK }}>{strat.client_note}</p>
        </div>
      ) : null}
      {strat.summary ? (
        <Card className="mb-4">
          <div className="eyebrow" style={{ color: MUT }}>Come è stata costruita</div>
          <p className="mt-2 whitespace-pre-line" style={{ color: INK2 }}>{strat.summary}</p>
        </Card>
      ) : null}

      {/* Feedback box */}
      <Card className="mb-6">
        {fb ? (
          <div data-testid="pricing-feedback-state">
            <div className="flex items-center gap-2" style={{ color: fb.state === "confirmed" ? "#4F9D69" : "#D4A359" }}>
              {fb.state === "confirmed" ? <CheckCircle2 className="h-5 w-5" /> : <Pencil className="h-5 w-5" />}
              <span className="font-display text-lg" style={{ color: INK }}>
                {fb.state === "confirmed" ? "Hai confermato questa strategia" : "Hai richiesto delle modifiche"}
              </span>
            </div>
            {fb.message ? <p className="mt-2 text-sm" style={{ color: INK2 }}>“{fb.message}”</p> : null}
            <button onClick={() => setMode(mode ? null : "changes")} className="mt-3 text-sm underline" style={{ color: MUT }}>
              {mode ? "Annulla" : "Invia un nuovo riscontro"}
            </button>
          </div>
        ) : (
          <div>
            <div className="font-display text-lg" style={{ color: INK }}>Cosa ne pensi di questa strategia?</div>
            <p className="mt-1 text-sm" style={{ color: MUT }}>Confermala per darci il via libera, oppure chiedici delle modifiche.</p>
          </div>
        )}

        {(!fb || mode) ? (
          <div className="mt-4">
            {mode === "changes" ? (
              <div data-testid="pricing-changes-form">
                <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3}
                  placeholder="Scrivi cosa vorresti cambiare (es. prezzi più bassi a gennaio)…"
                  className="w-full rounded-xl bg-[#1C1815] border px-4 py-3 outline-none focus:border-[#E05A2C] transition-colors"
                  style={{ borderColor: BORDER, color: INK }} />
                <div className="mt-3 flex gap-2 justify-end">
                  <button onClick={() => { setMode(null); setMsg(""); }} className="rounded-full px-4 py-2 text-sm" style={{ border: `1px solid ${BORDER}`, color: INK2 }}>Annulla</button>
                  <button onClick={() => submit("changes_requested")} disabled={sending || !msg.trim()} data-testid="pricing-send-changes"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium text-white disabled:opacity-50" style={{ background: BR }}>
                    <Send className="h-4 w-4" /> {sending ? "Invio…" : "Invia richiesta"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button onClick={() => submit("confirmed")} disabled={sending} data-testid="pricing-confirm-btn"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-white disabled:opacity-50" style={{ background: "#3B8C57" }}>
                  <CheckCircle2 className="h-4 w-4" /> Confermo la strategia
                </button>
                <button onClick={() => setMode("changes")} data-testid="pricing-request-btn"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium" style={{ border: `1px solid ${BORDER}`, color: INK }}>
                  <Pencil className="h-4 w-4" style={{ color: BR }} /> Chiedi modifiche
                </button>
              </div>
            )}
          </div>
        ) : null}
      </Card>

      {/* Tariffe mese per mese */}
      <div className="space-y-4">
        {MONTHS_IT.map((mName, mi) => {
          const list = byMonth[mi + 1] || [];
          if (!list.length) return null;
          return (
            <Card key={mi}>
              <div className="flex items-center gap-2 eyebrow" style={{ color: BR }}>
                <Calendar className="h-4 w-4" /> {mName}
              </div>
              <div className="mt-3 space-y-3">
                {list.map((r, i) => (
                  <div key={i} className="rounded-xl border p-4" style={{ borderColor: BORDER }}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-medium" style={{ color: INK }}>{r.unit}</div>
                        {r.band ? <div className="font-mono text-[10px] uppercase tracking-widest mt-0.5" style={{ color: MUT }}>{r.band}</div> : null}
                      </div>
                      <div className="flex gap-6">
                        <div className="text-right">
                          <div className="eyebrow" style={{ color: MUT }}>Feriale</div>
                          <div className="font-display text-xl" style={{ color: INK }}>{r.bar_weekday != null ? eur(r.bar_weekday) : "—"}</div>
                        </div>
                        <div className="text-right">
                          <div className="eyebrow" style={{ color: MUT }}>Weekend</div>
                          <div className="font-display text-xl" style={{ color: BR }}>{r.bar_weekend != null ? eur(r.bar_weekend) : "—"}</div>
                        </div>
                      </div>
                    </div>
                    {r.note ? <p className="mt-3 text-sm" style={{ color: INK2 }}>{r.note}</p> : null}
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Download PDF */}
      <div className="mt-6 mb-16">
        <button onClick={() => onDownloadPricingPdf && onDownloadPricingPdf(strat.year)} data-testid="pricing-download-pdf"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
          style={{ border: `1px solid ${BORDER}`, color: INK }}>
          <FileText className="h-4 w-4" style={{ color: BR }} /> Scarica il piano giorno-per-giorno (PDF)
        </button>
      </div>
    </div>
  );
}

const OPP_COLOR = {
  "festività": "#D4604A", "ponte": "#E0953A", "festa locale": "#C98A2B",
  "evento": "#8B6FB0", "voli": "#4F9D8E", "stagione": "#4F9D69",
};

function OpportunitiesView({ opportunities = [], pricing = [], onDownloadOpportunitiesPdf = null, onRefreshAir = null }) {
  const years = useMemo(() => opportunities.map((o) => o.year), [opportunities]);
  const [year, setYear] = useState(null);
  useEffect(() => {
    if (years.length && (year == null || !years.includes(year))) setYear(years[0]);
  }, [years, year]);
  const sel = useMemo(() => opportunities.find((o) => o.year === year) || null, [opportunities, year]);
  const [airBusy, setAirBusy] = useState(false);

  // Recommended rates (from the published pricing strategy of the same year), grouped by month.
  const ratesByMonth = useMemo(() => {
    const strat = pricing.find((p) => p.year === year);
    const map = {};
    (strat?.rows || []).forEach((r) => { (map[r.month] = map[r.month] || []).push(r); });
    return map;
  }, [pricing, year]);

  if (!sel) {
    return (
      <div className="mt-10 text-center py-16 rounded-xl border" style={{ borderColor: BORDER, color: MUT }}>
        Nessuna opportunità pubblicata al momento.
      </div>
    );
  }

  const byMonth = {};
  (sel.items || []).forEach((it) => { (byMonth[it.month] = byMonth[it.month] || []).push(it); });
  const impactColor = (imp) => imp === "alto" ? "#4F9D69" : imp === "medio" ? "#C98A2B" : MUT;

  return (
    <div className="mt-8" data-testid="client-opportunities">
      {years.length > 1 ? (
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <span className="text-xs font-mono uppercase tracking-widest" style={{ color: MUT }}>Anno</span>
          {years.map((y) => (
            <button key={y} onClick={() => setYear(y)} className="rounded-full px-4 py-1.5 text-sm font-medium transition-colors"
              style={{ background: y === year ? BR : "transparent", color: y === year ? "#fff" : INK2, border: `1px solid ${y === year ? BR : BORDER}` }}>
              {y}
            </button>
          ))}
        </div>
      ) : null}

      <SectionHead n="✦" title={`Opportunità di vendita ${sel.year}`} hint="I periodi su cui puntare: festività, ponti, feste, eventi" />

      {sel.air_connectivity && sel.air_connectivity.airport ? (
        <Card className="mb-5" data-testid="client-air-connectivity">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 eyebrow" style={{ color: BR }}>
              <Plane className="h-4 w-4" /> Afflusso aereo · {sel.air_connectivity.airport}
            </div>
            {onRefreshAir ? (
              <button onClick={async () => { setAirBusy(true); try { await onRefreshAir(sel.year); } finally { setAirBusy(false); } }}
                disabled={airBusy} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium disabled:opacity-50 transition-colors" style={{ border: `1px solid ${BR}`, color: BR }} data-testid="client-air-refresh">
                <Plane className="h-3.5 w-3.5" /> {airBusy ? "Aggiorno…" : "Aggiorna check voli"}
              </button>
            ) : null}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {MONTHS_IT.map((m, idx) => {
              const lvl = (sel.air_connectivity.by_month || {})[String(idx + 1)] || "—";
              const col = lvl === "alta" ? "#4F9D69" : lvl === "media" ? "#C98A2B" : lvl === "bassa" ? "#9A8F7E" : MUT;
              return (
                <div key={idx} className="rounded-lg px-2 py-1 text-center" style={{ border: `1px solid ${BORDER}`, minWidth: 54 }}>
                  <div className="text-[10px]" style={{ color: MUT }}>{m.slice(0, 3)}</div>
                  <div className="text-[11px] font-medium capitalize" style={{ color: col }}>{lvl}</div>
                </div>
              );
            })}
          </div>
          {sel.air_connectivity.note ? <p className="mt-3 text-sm" style={{ color: INK2 }}>{sel.air_connectivity.note}</p> : null}
        </Card>
      ) : onRefreshAir ? (
        <div className="mb-5">
          <button onClick={async () => { setAirBusy(true); try { await onRefreshAir(sel.year); } finally { setAirBusy(false); } }}
            disabled={airBusy} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm disabled:opacity-50" style={{ border: `1px solid ${BORDER}`, color: INK }} data-testid="client-air-check">
            <Plane className="h-4 w-4" style={{ color: BR }} /> {airBusy ? "Controllo i voli…" : "Controlla i voli dell'aeroporto più vicino"}
          </button>
        </div>
      ) : null}


      <div className="space-y-4">
        {MONTHS_IT.map((mName, mi) => {
          const list = byMonth[mi + 1] || [];
          if (!list.length) return null;
          return (
            <Card key={mi}>
              <div className="flex items-center gap-2 eyebrow" style={{ color: BR }}>
                <Calendar className="h-4 w-4" /> {mName}
              </div>
              <div className="mt-3 space-y-3">
                {list.map((it, i) => (
                  <div key={i} className="rounded-xl border p-4" style={{ borderColor: BORDER }}>
                    <div className="flex items-start justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {it.kind ? (
                          <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                            style={{ color: OPP_COLOR[it.kind] || MUT, border: `1px solid ${(OPP_COLOR[it.kind] || MUT)}55` }}>{it.kind}</span>
                        ) : null}
                        <span className="font-medium" style={{ color: INK }}>{it.title}</span>
                        {it.label ? <span className="text-sm" style={{ color: MUT }}>· {it.label}</span> : null}
                      </div>
                      {it.impact ? (
                        <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: impactColor(it.impact) }}>Impatto {it.impact}</span>
                      ) : null}
                    </div>
                    {it.detail ? <p className="mt-2 text-sm" style={{ color: INK2 }}>{it.detail}</p> : null}
                  </div>
                ))}
              </div>
              {(ratesByMonth[mi + 1] || []).length ? (
                <div className="mt-3 rounded-xl p-3" style={{ background: "#2B1B15", border: `1px solid #4D2C20` }} data-testid={`opp-rates-${mi + 1}`}>
                  <div className="eyebrow flex items-center gap-2" style={{ color: BR }}>
                    <TrendingUp className="h-3.5 w-3.5" /> Tariffa consigliata del periodo
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {ratesByMonth[mi + 1].map((r, k) => (
                      <div key={k} className="flex items-center justify-between text-sm">
                        <span style={{ color: INK }}>{r.unit}</span>
                        <span className="font-mono" style={{ color: INK2 }}>
                          Feriale <span style={{ color: INK }}>{r.bar_weekday != null ? eur(r.bar_weekday) : "—"}</span>
                          <span style={{ color: MUT }}> · </span>
                          Weekend <span style={{ color: BR }}>{r.bar_weekend != null ? eur(r.bar_weekend) : "—"}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </Card>
          );
        })}
      </div>
      <div className="mt-6 mb-16">
        <button onClick={() => onDownloadOpportunitiesPdf && onDownloadOpportunitiesPdf(sel.year)} data-testid="opportunities-download-pdf"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
          style={{ border: `1px solid ${BORDER}`, color: INK }}>
          <FileText className="h-4 w-4" style={{ color: BR }} /> Scarica le opportunità in PDF (stampabile)
        </button>
      </div>
    </div>
  );
}

